import { supabase } from "@/lib/supabase";
import { isSupabaseConfigured } from "@/lib/env";
import { invokeFunction } from "@/lib/api";
import type { ContactInput } from "@/features/forms/schemas";

export type ContactMessageStatus = "new" | "in_review" | "closed";

export interface ContactMessageNote {
  id: string;
  messageId: string;
  authorId: string | null;
  authorName: string;
  authorEmail: string;
  body: string;
  createdAt: string;
  canDelete: boolean;
}

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status: ContactMessageStatus;
  notes: ContactMessageNote[];
  /** Legacy free-text notes from before notes were author-attributed. */
  internalNotes: string;
  createdAt: string;
  updatedAt: string;
}

interface ContactMessageRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  status: ContactMessageStatus;
  internal_notes: string | null;
  created_at: string;
  updated_at: string;
  notes?: ContactMessageNoteRow[];
}

interface ContactMessageNoteRow {
  id: string;
  message_id: string;
  author_id: string | null;
  author_name: string;
  author_email: string | null;
  body: string;
  created_at: string;
  can_delete?: boolean;
}

const LS_KEY = "bb_contact_messages";

const seedMessages: ContactMessage[] = [
  {
    id: "msg-1001",
    name: "Jordan Miller",
    email: "jordan.shipper@example.com",
    phone: "+1 (312) 555-0118",
    message:
      "Need tanker capacity for weekly food-grade liquid loads from Aurora, IL to Columbus, OH. Please call with lane pricing.",
    status: "new",
    notes: [],
    internalNotes: "",
    createdAt: "2026-06-13T15:35:00.000Z",
    updatedAt: "2026-06-13T15:35:00.000Z",
  },
  {
    id: "msg-1002",
    name: "Maria Lopez",
    email: "maria.broker@example.com",
    phone: "+1 (708) 555-0133",
    message:
      "Looking for hazmat tanker coverage next week. Can dispatch confirm availability and required paperwork?",
    status: "in_review",
    notes: [
      {
        id: "note-1002-1",
        messageId: "msg-1002",
        authorId: "dev-internal",
        authorName: "Preview Staff",
        authorEmail: "staff@bullandbearco.com",
        body: "Need to confirm hazmat availability before replying.",
        createdAt: "2026-06-12T19:45:00.000Z",
        canDelete: true,
      },
    ],
    internalNotes: "Need to confirm hazmat availability before replying.",
    createdAt: "2026-06-12T19:10:00.000Z",
    updatedAt: "2026-06-12T19:45:00.000Z",
  },
];

const supabaseReady = () => isSupabaseConfigured && !!supabase;

const byNewest = (a: ContactMessage, b: ContactMessage) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const byNewestNote = (a: ContactMessageNote, b: ContactMessageNote) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

function rowToMessage(row: ContactMessageRow): ContactMessage {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone ?? "",
    message: row.message,
    status: row.status,
    notes: (row.notes ?? []).map(rowToNote).sort(byNewestNote),
    internalNotes: row.internal_notes ?? "",
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToNote(row: ContactMessageNoteRow): ContactMessageNote {
  return {
    id: row.id,
    messageId: row.message_id,
    authorId: row.author_id,
    authorName: row.author_name,
    authorEmail: row.author_email ?? "",
    body: row.body,
    createdAt: row.created_at,
    canDelete: Boolean(row.can_delete),
  };
}

function localReadAll(): ContactMessage[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      return (JSON.parse(raw) as ContactMessage[]).map((message) => ({
        ...message,
        notes: (message.notes ?? []).sort(byNewestNote),
      }));
    }
  } catch {
    /* ignore malformed cache */
  }
  localStorage.setItem(LS_KEY, JSON.stringify(seedMessages));
  return [...seedMessages];
}

function localWriteAll(messages: ContactMessage[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(messages));
}

const localId = (prefix: string) =>
  `${prefix}-${typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Date.now()}`;

export async function listContactMessages(): Promise<ContactMessage[]> {
  if (supabaseReady()) {
    const { messages } = await invokeFunction<{ messages: ContactMessageRow[] }>(
      "messages-admin",
      { action: "list" },
    );
    return messages.map(rowToMessage);
  }
  return localReadAll().sort(byNewest);
}

export async function updateContactMessage(
  id: string,
  changes: { status?: ContactMessageStatus },
): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("messages-admin", {
      action: "update",
      id,
      ...(changes.status ? { status: changes.status } : {}),
    });
    return;
  }

  const now = new Date().toISOString();
  localWriteAll(
    localReadAll().map((message) =>
      message.id === id
        ? {
            ...message,
            status: changes.status ?? message.status,
            updatedAt: now,
          }
        : message,
    ),
  );
}

export async function addContactMessageNote(id: string, body: string): Promise<void> {
  const noteBody = body.trim();
  if (!noteBody) return;

  if (supabaseReady()) {
    await invokeFunction("messages-admin", {
      action: "addNote",
      id,
      body: noteBody,
    });
    return;
  }

  const now = new Date().toISOString();
  localWriteAll(
    localReadAll().map((message) =>
      message.id === id
        ? {
            ...message,
            notes: [
              ...message.notes,
              {
                id: localId("note"),
                messageId: id,
                authorId: "dev-internal",
                authorName: "Preview Staff",
                authorEmail: "staff@bullandbearco.com",
                body: noteBody,
                createdAt: now,
                canDelete: true,
              },
            ],
            internalNotes: noteBody,
            updatedAt: now,
          }
        : message,
    ),
  );
}

export async function deleteContactMessageNote(messageId: string, noteId: string): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("messages-admin", {
      action: "deleteNote",
      id: messageId,
      noteId,
    });
    return;
  }

  localWriteAll(
    localReadAll().map((message) =>
      message.id === messageId
        ? {
            ...message,
            notes: message.notes.filter((note) => note.id !== noteId || !note.canDelete),
            updatedAt: new Date().toISOString(),
          }
        : message,
    ),
  );
}

export async function createLocalContactMessage(values: ContactInput): Promise<void> {
  const now = new Date().toISOString();
  const message: ContactMessage = {
    id: localId("msg"),
    name: values.name,
    email: values.email,
    phone: values.phone ?? "",
    message: values.message,
    status: "new",
    notes: [],
    internalNotes: "",
    createdAt: now,
    updatedAt: now,
  };

  localWriteAll([message, ...localReadAll()]);
}
